/**
 * Registers and configures a minimal Video.js plugin set for stitched HLS playback
 * (matches main VideoPlayer seek / volume / play-from-start / spacebar — no SMF, Oracle, etc.)
 */
import { join as joinPromise } from 'bluebird'
import { Map } from 'immutable'
import replace from 'lodash/replace'

const KEY_SPACEBAR = 32

function getVideoJs () {
  if (typeof global !== 'undefined' && global.videojs) {
    return global.videojs
  }
  if (typeof window !== 'undefined' && window.videojs) {
    return window.videojs
  }
  return null
}

/**
 * Idempotent plugin registration (same pattern as `initVideoJsPlugins` in VideoPlayer).
 * @returns {Promise<void>}
 */
export async function registerStitchedPlayerPlugins () {
  const vjs = getVideoJs()
  if (!vjs || typeof vjs.getPlugins !== 'function') {
    return
  }

  const [
    SeekButtonsPlugin,
    KeyboardPlaybackTogglePlugin,
    VolumeBarPlugin,
    PlayFromBeginningButtonPlugin,
  ] = await joinPromise(
    import('../../video-js/seek-buttons/index'),
    import('../../video-js/keyboard-playback-toggle/index'),
    import('../../video-js/volume-bar/index'),
    import('../../video-js/play-from-beginning-button/index'),
  )

  const registeredPlugins = vjs.getPlugins()
  if (registeredPlugins[SeekButtonsPlugin.PLUGIN_NAME] === undefined) {
    SeekButtonsPlugin.register(vjs)
  }
  if (registeredPlugins[KeyboardPlaybackTogglePlugin.PLUGIN_NAME] === undefined) {
    KeyboardPlaybackTogglePlugin.register(vjs)
  }
  if (registeredPlugins[VolumeBarPlugin.PLUGIN_NAME] === undefined) {
    VolumeBarPlugin.register(vjs)
  }
  if (registeredPlugins[PlayFromBeginningButtonPlugin.PLUGIN_NAME] === undefined) {
    PlayFromBeginningButtonPlugin.register(vjs)
  }
}

/**
 * @param {*} player Video.js player instance
 * @param {import('immutable').Map} staticText Semantic Stitch static text (`semanticStitchPage`)
 */
export function configureStitchedPlayerPlugins (player, staticText) {
  const data = staticText.get('data', Map())
  const forward = 30
  const back = 10
  // eslint-disable-next-line no-template-curly-in-string
  const seekBackLabel = replace(data.get('seekBack', ''), '${ seekBack }', back)
  // eslint-disable-next-line no-template-curly-in-string
  const seekForwardLabel = replace(data.get('seekForward', ''), '${ seekForward }', forward)

  player.KeyboardPlaybackToggle({ keys: [KEY_SPACEBAR] })
  player.SeekButtons({
    forward,
    back,
    seekBack: seekBackLabel,
    seekForward: seekForwardLabel,
  })
  player.VolumeBar({
    staticText: Map({
      mute: data.get('mute', 'Mute'),
    }),
  })
  player.PlayFromBeginningButton()

  const playFromBeginningLabel = data.get('playFromBeginning', 'Play from beginning')
  const pfb = player.controlBar.getChild('PlayFromBeginningButton')
  if (pfb) {
    pfb.controlText(playFromBeginningLabel)
  }
}
